/**
 * The `layout` domain catalog — the browsable archetypes, the canonical
 * section list, and (crucially) the LAYOUT → DESIGN PAIRING.
 *
 * A layout recipe only RESTRUCTURES the tree (spans + a Grid/Columns wrapper).
 * On its own a bento grid of unstyled blocks doesn't "lay out correctly" — it
 * needs the matching radius/shadow/surface/border tokens. So every archetype
 * names a `recommendedPresetId`: a style preset that completes the look. The
 * consumer (studio-ui) owns the actual token VALUES (see
 * `editor/theme/themePresets*`); this catalog references them by id, keeping
 * the package pure (no `ThemeTokens` / font dependency) and drift-free.
 *
 * Apply flow (studio-ui): picking an archetype applies `recipeId` (undoable
 * tree transform) AND, unless the user opts out, selects/previews
 * `recommendedPresetId` so the page is both restructured and re-styled.
 */
import type { LayoutRecipe } from "./recipes.js";
import { LAYOUT_RECIPES } from "./recipes.js";

/** A reusable page section (metadata only — the builder owns the actual nodes). */
export interface LayoutSection {
  id: string;
  label: string;
  tags: string[];
  /** Registry component(s) this section is typically built from. */
  components: string[];
}

/** A layout archetype the user browses in the Layouts gallery. */
export interface LayoutArchetype {
  id: string;
  label: string;
  description: string;
  /** One-line human structure summary for the gallery card. */
  structure: string;
  tags: string[];
  /** Grouping shown in the gallery (e.g. "Grid", "Editorial", "Minimal"). */
  category: string;
  /** The recipe applied when chosen (an id from recipes.ts, incl. "stack"). */
  recipeId: string;
  /**
   * THE PAIRING — the studio-ui style-preset id auto-applied with this layout
   * so the structure reads correctly. Resolved to tokens by the consumer.
   */
  recommendedPresetId: string;
  /** Secondary presets surfaced as alternatives (not auto-applied). */
  alternativePresetIds?: string[];
  /** False ⇒ recipe not shippable yet; the gallery shows a "soon" badge. */
  buildable: boolean;
}

/** A built-in style preset the layouts pair with. This is a lightweight INDEX
 *  (id + label + category) — the actual `--dl-*` token VALUES live in studio-ui
 *  (editor/theme/themePresets*), resolved by id. Lets the catalog + MCP describe
 *  the available designs without dragging token/font deps into this package. */
export interface StylePresetRef {
  id: string;
  label: string;
  category: string;
}

export interface LayoutCatalog {
  version: string;
  layouts: LayoutArchetype[];
  recipes: Array<Pick<LayoutRecipe, "id" | "label" | "description">>;
  sections: LayoutSection[];
  stylePresets: StylePresetRef[];
}

/* ------------------------------- archetypes ------------------------------- */

export const LAYOUTS: LayoutArchetype[] = [
  {
    id: "bento",
    label: "Bento mosaic",
    description:
      "An asymmetric tile grid with one large focal cell — modern, editorial-tech, scannable.",
    structure: "4-col grid · hero is a 2×2 focal tile · the rest alternate wide/narrow cells.",
    tags: ["grid", "asymmetric", "modern", "dashboard"],
    category: "Grid",
    recipeId: "bento",
    recommendedPresetId: "bento",
    alternativePresetIds: ["soft-pop", "glassmorphism", "dark-mesh"],
    buildable: true,
  },
  {
    id: "editorial",
    label: "Editorial spread",
    description:
      "A magazine layout — a commanding full-width hero over a two-column reading body.",
    structure: "Full-width hero · 2-column body · full-width closing block.",
    tags: ["editorial", "magazine", "content", "long-form"],
    category: "Editorial",
    recipeId: "editorial",
    recommendedPresetId: "editorial-serif",
    alternativePresetIds: ["big-type"],
    buildable: true,
  },
  {
    id: "feature-grid",
    label: "Feature grid",
    description:
      "A full-width hero above an even three-column card grid — perfect for features, services, or a gallery.",
    structure: "Full-width hero · even 3-column card grid for everything below.",
    tags: ["grid", "features", "cards", "saas"],
    category: "Grid",
    recipeId: "feature-grid",
    recommendedPresetId: "soft-pop",
    alternativePresetIds: ["corporate-clean", "glassmorphism", "swiss"],
    buildable: true,
  },
  {
    id: "brutalist",
    label: "Brutalist stack",
    description:
      "Stark, gapless full-width blocks — high-contrast, hard-edged, unmissable.",
    structure: "Single column · every block full-width and flush (no gaps).",
    tags: ["brutalist", "bold", "high-contrast", "stark"],
    category: "Statement",
    recipeId: "brutalist",
    recommendedPresetId: "brutalist",
    alternativePresetIds: ["big-type"],
    buildable: true,
  },
  {
    id: "stack",
    label: "Clean stack",
    description:
      "A calm single column — the classic, readable default. Also resets any grid layout.",
    structure: "Single column · every block full-width with comfortable spacing.",
    tags: ["minimal", "single-column", "classic", "reset"],
    category: "Minimal",
    recipeId: "stack",
    recommendedPresetId: "minimal",
    alternativePresetIds: ["corporate-clean"],
    buildable: true,
  },
];

/* -------------------------------- sections -------------------------------- */
/** Canonical landing-page sections (CRO-ordered). Metadata for the AI + UI —
 *  the builder composes the actual nodes from these component names. */
export const SECTIONS: LayoutSection[] = [
  { id: "hero", label: "Hero", tags: ["above-fold", "headline"], components: ["Hero"] },
  { id: "logos", label: "Logo bar", tags: ["social-proof"], components: ["LogoBar"] },
  { id: "features", label: "Features", tags: ["value-prop"], components: ["Grid", "Text", "Image"] },
  { id: "stats", label: "Stats", tags: ["credibility"], components: ["Stats"] },
  { id: "testimonials", label: "Testimonials", tags: ["social-proof"], components: ["Testimonials"] },
  { id: "pricing", label: "Pricing", tags: ["conversion"], components: ["Pricing"] },
  { id: "faq", label: "FAQ", tags: ["objection-handling"], components: ["FAQ"] },
  { id: "cta", label: "Final CTA", tags: ["conversion"], components: ["CTAButton", "Form"] },
  { id: "footer", label: "Footer", tags: ["nav", "legal"], components: ["Footer"] },
];

/* ----------------------------- style presets ------------------------------ */
/** The built-in designs the archetypes pair with (id matches studio-ui presets). */
export const STYLE_PRESETS: StylePresetRef[] = [
  { id: "bento", label: "Bento", category: "Modern" },
  { id: "soft-pop", label: "Soft Pop", category: "Playful" },
  { id: "glassmorphism", label: "Glassmorphism", category: "Modern" },
  { id: "editorial-serif", label: "Editorial", category: "Editorial" },
  { id: "big-type", label: "Big Type", category: "Editorial" },
  { id: "brutalist", label: "Brutalist", category: "Statement" },
  { id: "minimal", label: "Minimal", category: "Minimal" },
  { id: "corporate-clean", label: "Corporate", category: "Professional" },
  { id: "gradient-vivid", label: "Gradient", category: "Vivid" },
  { id: "swiss", label: "Swiss", category: "Minimal" },
  { id: "dark-mesh", label: "Dark Mesh", category: "Vivid" },
];

/* --------------------------------- catalog -------------------------------- */

export const LAYOUT_CATALOG: LayoutCatalog = {
  version: "1.0",
  layouts: LAYOUTS,
  recipes: LAYOUT_RECIPES,
  sections: SECTIONS,
  stylePresets: STYLE_PRESETS,
};

/** Look up an archetype by id, or undefined. */
export function getLayout(id: string): LayoutArchetype | undefined {
  return LAYOUTS.find((l) => l.id === id);
}

/** The recommended (and alternative) preset ids paired with a layout. */
export function pairedPresetIds(layoutId: string): string[] {
  const l = getLayout(layoutId);
  if (!l) return [];
  return [l.recommendedPresetId, ...(l.alternativePresetIds ?? [])];
}
